import { useState, useEffect } from 'react';

export default function Hero() {
  const [isScrolled, setIsScrolled] = useState(false);
  const [showCTA, setShowCTA] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
      setShowCTA(window.scrollY > 600);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const scrollTo = (id: string) => {
    const el = document.getElementById(id);
    if (el) {
      el.scrollIntoView({ behavior: 'smooth' });
    }
    setMobileMenuOpen(false);
  };

  const navLinks = [
    { label: 'Course Overview', path: '/course-overview' },
    { label: 'Why This Course', id: 'why-this-course' },
    { label: "Who It's For", id: 'who-its-for' },
    { label: 'Pricing', id: 'pricing-section' },
    { label: 'Contact', id: 'contact-section' },
  ];

  const handleNavClick = (link: { label: string; id?: string; path?: string }) => {
    if (link.path) {
      if (window.REACT_APP_NAVIGATE) {
        window.REACT_APP_NAVIGATE(link.path);
      }
    } else if (link.id) {
      scrollTo(link.id);
    }
    setMobileMenuOpen(false);
  };

  return (
    <section className="relative min-h-screen overflow-hidden">
      {/* Background Image */}
      <div className="absolute inset-0 w-full h-full">
        <img
          src="https://static.readdy.ai/image/597b2346b2ff492b6c1bd141556b2fdd/4c10202d27d20381b0a86768c60a8d14.png"
          alt="Australian sandstone coastline at sunset — connecting with Country"
          className="w-full h-full object-cover object-center"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-black/60 via-black/50 to-black/70"></div>
      </div>

      {/* Navigation */}
      <nav
        className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
          isScrolled ? 'bg-white shadow-md' : 'bg-transparent'
        }`}
      >
        <div className="max-w-7xl mx-auto px-6 lg:px-12 py-4 flex items-center justify-between">
          {/* Logo */}
          <div className="flex items-center flex-shrink-0">
            <img
              src="https://static.readdy.ai/image/597b2346b2ff492b6c1bd141556b2fdd/301672d98dde029302942d99224febda.png"
              alt="Logo"
              className="h-11 w-auto object-contain"
            />
          </div>

          {/* Desktop Nav Links */}
          <div className="hidden md:flex items-center gap-1">
            {navLinks.map((link) => (
              <button
                key={link.label}
                onClick={() => handleNavClick(link)}
                className={`px-4 py-2 rounded-lg text-sm font-medium transition-all whitespace-nowrap cursor-pointer ${
                  isScrolled
                    ? 'text-[#3D1203] hover:text-[#BA3D03] hover:bg-[#FFF9F5]'
                    : 'text-white/90 hover:text-white hover:bg-white/10'
                }`}
              >
                {link.label}
              </button>
            ))}
          </div>

          {/* Desktop CTA area */}
          <div className="hidden md:flex items-center gap-3">
            <div
              className={`transition-all duration-500 ${
                showCTA ? 'opacity-0 scale-90 w-0 overflow-hidden' : 'opacity-100 scale-100'
              }`}
            >
              <button
                onClick={() => scrollTo('contact-section')}
                className="px-5 py-2.5 bg-[#BA3D03] text-white rounded-lg text-sm font-semibold hover:bg-[#3D1203] transition-all whitespace-nowrap cursor-pointer shadow-sm"
              >
                Get in Touch
              </button>
            </div>
            <div
              className={`transition-all duration-500 ${
                showCTA
                  ? 'opacity-100 translate-x-0 scale-100'
                  : 'opacity-0 translate-x-4 scale-90 w-0 overflow-hidden pointer-events-none'
              }`}
            >
              <button
                onClick={() => scrollTo('contact-section')}
                className="relative px-6 py-2.5 bg-[#BA3D03] text-white rounded-lg text-sm font-bold hover:bg-[#3D1203] transition-all whitespace-nowrap cursor-pointer shadow-md hover:shadow-lg group"
              >
                <span className="absolute inset-0 rounded-lg animate-ping-slow bg-[#BA3D03]/30"></span>
                <span className="relative flex items-center gap-2">
                  <i className="ri-price-tag-3-line text-base w-4 h-4 flex items-center justify-center"></i>
                  Request Company Pricing
                </span>
              </button>
            </div>
          </div>

          {/* Mobile Hamburger */}
          <button
            className={`md:hidden w-9 h-9 flex items-center justify-center rounded-lg transition-colors cursor-pointer ${
              isScrolled ? 'text-[#0E0503] hover:bg-[#FFF9F5]' : 'text-white hover:bg-white/10'
            }`}
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            aria-label="Toggle menu"
          >
            <i className={`text-xl ${mobileMenuOpen ? 'ri-close-line' : 'ri-menu-line'}`}></i>
          </button>
        </div>

        {/* Mobile Menu Dropdown */}
        {mobileMenuOpen && (
          <div className="md:hidden bg-white border-t border-[#E8C580] shadow-lg">
            <div className="max-w-7xl mx-auto px-6 py-4 flex flex-col gap-1">
              {navLinks.map((link) => (
                <button
                  key={link.label}
                  onClick={() => handleNavClick(link)}
                  className="text-left px-4 py-3 rounded-lg text-sm font-medium text-[#3D1203] hover:text-[#BA3D03] hover:bg-[#FFF9F5] transition-all whitespace-nowrap cursor-pointer"
                >
                  {link.label}
                </button>
              ))}
              <button
                onClick={() => scrollTo('contact-section')}
                className="mt-2 px-4 py-3 bg-[#BA3D03] text-white rounded-lg text-sm font-bold hover:bg-[#3D1203] transition-all whitespace-nowrap cursor-pointer text-center flex items-center justify-center gap-2"
              >
                <i className="ri-price-tag-3-line"></i>
                Request Company Pricing
              </button>
            </div>
          </div>
        )}
      </nav>

      {/* Hero Content */}
      <div className="relative z-10 w-full min-h-screen flex items-center">
        <div className="max-w-7xl mx-auto px-5 lg:px-12 pt-28 pb-16 lg:pb-32 w-full">
          <div className="max-w-3xl">
            <div className="inline-flex items-center px-3 py-1.5 sm:px-4 sm:py-2 bg-[#E48523] text-white rounded-full text-xs sm:text-sm font-medium mb-6 sm:mb-8">
              NSW Built Environment | Self-Paced Online Course
            </div>

            <h1 className="text-3xl sm:text-5xl lg:text-7xl font-bold text-white leading-tight mb-5 sm:mb-8">
              Designing and Building with Country: Indigenous Perspectives in Design
            </h1>

            <p className="text-base sm:text-xl lg:text-2xl text-white/80 mb-8 sm:mb-12 max-w-2xl leading-relaxed">
              Practical self-paced training for NSW project teams to build genuine cultural
              capability — embedding Country-led thinking deeply into planning, design and delivery.
            </p>

            <div className="flex flex-col sm:flex-row gap-3 sm:gap-4 mb-6 sm:mb-8">
              <button
                onClick={() => scrollTo('contact-section')}
                className="w-full sm:w-auto px-8 py-4 bg-[#BA3D03] text-white rounded-lg text-base font-semibold hover:bg-[#3D1203] transition-all shadow-lg hover:shadow-xl whitespace-nowrap cursor-pointer"
              >
                Enquire for Company Access
              </button>
            </div>

            <div className="mb-5 sm:mb-6">
              <button
                onClick={() => {
                  if (window.REACT_APP_NAVIGATE) {
                    window.REACT_APP_NAVIGATE('/course-overview');
                  }
                }}
                className="text-white/70 hover:text-white text-sm font-medium transition-colors cursor-pointer underline underline-offset-4 decoration-white/40 hover:decoration-white/80 whitespace-nowrap"
              >
                View course overview and module breakdown →
              </button>
            </div>

            <p className="text-xs sm:text-sm text-white/60 leading-relaxed">
              Contact-first enquiry • Tailored company pricing • 12-month access • Access code/link
              issued after payment
            </p>
          </div>
        </div>

        {/* Photo attribution */}
        <p className="absolute bottom-4 right-4 text-white/50 text-xs font-light tracking-wide pointer-events-none select-none">
          Source: Charles Trindall 2024
        </p>
      </div>
    </section>
  );
}
